// Social handle ↔ URL helpers for the coach edit form.
//
// STORAGE DOES NOT CHANGE. `coaches.instagram_url` / `youtube_url` /
// `facebook_url` keep holding absolute https URLs. The form strips the
// platform prefix on hydrate so a coach types only their handle, and
// recomposes on save. That leaves `validateHttpsUrl` in
// functions/api/coach.ts, SocialIcons.tsx and the public profile render
// completely untouched — no server change, no migration.
//
// Why this is not just `url.split('/').pop()`:
//   - Instagram stores as `https://www.instagram.com/kaillum/` — the `www.`
//     and the TRAILING SLASH both have to be tolerated.
//   - YouTube has TWO shapes: `youtube.com/@handle` and
//     `youtube.com/channel/UC…`. A slug-only field silently corrupts channel
//     IDs, so those stay as full URLs.
//   - Facebook has `facebook.com/profile.php?id=<numeric>` on older accounts,
//     which is a query string, not a path segment.
//
// So for YouTube and Facebook the field accepts EITHER a bare handle OR a
// pasted full URL, and detects which. Instagram is handle-only in practice but
// tolerates a pasted URL through the same path.

export type SocialPlatform = 'instagram' | 'youtube' | 'facebook'

export const SOCIAL_PREFIX: Record<SocialPlatform, string> = {
  instagram: 'instagram.com/',
  youtube: 'youtube.com/',
  facebook: 'facebook.com/',
}

const HOST_RE: Record<SocialPlatform, RegExp> = {
  instagram: /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/(.*)$/i,
  youtube: /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/(.*)$/i,
  facebook: /^(?:https?:\/\/)?(?:www\.|m\.)?facebook\.com\/(.*)$/i,
}

/** Anything that already looks like a URL is passed through untouched. */
function looksLikeUrl(value: string): boolean {
  return /^https?:\/\//i.test(value) || /^[\w.-]+\.[a-z]{2,}\//i.test(value)
}

/**
 * YouTube paths that must NOT be reduced to a handle. `/channel/UC…` is an
 * opaque id; `/c/` and `/user/` are legacy custom-URL forms. Reducing any of
 * them to their last segment and recomposing as `/@segment` produces a dead
 * link.
 */
function isOpaqueYoutubePath(path: string): boolean {
  return /^(channel|c|user)\//i.test(path)
}

/** Facebook's numeric-id form is a query string, not a vanity slug. */
function isOpaqueFacebookPath(path: string): boolean {
  return /^profile\.php/i.test(path)
}

export interface StrippedSocial {
  /** What to show in the input: a bare handle, or a full URL when opaque. */
  value: string
  /** True when `value` is a full URL and the prefix label should be hidden. */
  isFullUrl: boolean
}

/**
 * Stored URL → what the input should display.
 *
 * Returns `isFullUrl: true` when the stored value cannot be safely reduced to
 * a handle, in which case the UI shows the whole URL and skips the prefix
 * label. A value on some other host is also returned whole rather than being
 * mangled into a handle for the wrong platform.
 */
export function stripSocial(
  platform: SocialPlatform,
  stored: string | null | undefined,
): StrippedSocial {
  const raw = (stored ?? '').trim()
  if (!raw) return { value: '', isFullUrl: false }

  const m = raw.match(HOST_RE[platform])
  if (!m) {
    // Stored on a host we don't recognise for this platform. Show it as-is;
    // do not guess.
    return { value: raw, isFullUrl: true }
  }
  // Drop query/hash for the handle case, and the trailing slash Instagram
  // stores. `path` keeps the query for the opaque checks below.
  const path = m[1]
  if (platform === 'youtube' && isOpaqueYoutubePath(path)) {
    return { value: raw, isFullUrl: true }
  }
  if (platform === 'facebook' && isOpaqueFacebookPath(path)) {
    return { value: raw, isFullUrl: true }
  }
  const handle = path.split(/[?#]/)[0].replace(/\/+$/, '')
  if (!handle) return { value: '', isFullUrl: false }
  return { value: handle, isFullUrl: false }
}

/**
 * What the input holds → what to store.
 *
 * Empty → null (clears the column). A pasted URL is used as-is, upgraded to
 * https when it arrived bare or as http. A bare handle is composed onto the
 * canonical host, with a leading '@' tolerated for Instagram/Facebook and
 * required-by-convention for YouTube handles.
 */
export function composeSocial(
  platform: SocialPlatform,
  input: string | null | undefined,
): string | null {
  const raw = (input ?? '').trim()
  if (!raw) return null

  if (looksLikeUrl(raw)) {
    // Already a URL — do not compose a second prefix onto it. Force https so
    // it still satisfies validateHttpsUrl server-side.
    if (/^http:\/\//i.test(raw)) return raw.replace(/^http:\/\//i, 'https://')
    if (/^https:\/\//i.test(raw)) return raw
    return `https://${raw}`
  }

  const handle = raw.replace(/^@+/, '').replace(/^\/+/, '').replace(/\/+$/, '')
  if (!handle) return null
  if (platform === 'youtube') {
    // YouTube handles are '@'-prefixed in their canonical URL form.
    return `https://www.youtube.com/@${handle}`
  }
  return `https://www.${platform}.com/${handle}`
}
